import { useEffect, useState } from 'react'
import { evalArithmetic, looksLikeExpression } from '../../utils/evalArithmetic'

/**
 * Drop-in replacement for <input type="number"> on data-entry forms.
 * Supports plain numbers AND arithmetic expressions like "120+30",
 * "150-20", "10*5", "100/4". Evaluates on blur, Enter, or Tab.
 *
 * Renders as type="text" with inputMode="decimal" so mobile users still
 * get the numeric keypad. type="number" cannot be used because it would
 * reject the operator characters.
 *
 * Props (all the standard <input> props plus):
 *   value:    current numeric value (number, string, null, or undefined)
 *   onChange: called with the resolved numeric value (NOT the event)
 *             — receives a number on success, null when cleared
 *   onBlur:   optional additional handler invoked after evaluation
 *
 * While the user is typing an expression, the input is given a subtle
 * teal border so they know it will be evaluated. The affordance vanishes
 * once the value is reduced to a plain number.
 */
export default function ArithmeticInput({
  value,
  onChange,
  onBlur,
  className = '',
  placeholder,
  disabled,
  ...rest
}) {
  const initial = value !== null && value !== undefined && value !== ''
    ? String(value)
    : ''
  const [display, setDisplay] = useState(initial)

  // Re-sync display when external value changes (e.g. date switch,
  // "Copy from prev day", parent state reset).
  useEffect(() => {
    setDisplay(
      value !== null && value !== undefined && value !== ''
        ? String(value)
        : ''
    )
  }, [value])

  function evaluate(currentDisplay) {
    const trimmed = String(currentDisplay).trim()

    if (trimmed === '') {
      setDisplay('')
      onChange(null)
      return
    }

    // Plain number fast path
    if (!looksLikeExpression(trimmed)) {
      const num = parseFloat(trimmed)
      if (!isNaN(num)) {
        setDisplay(String(num))
        onChange(num)
      } else {
        // Garbage input — revert
        setDisplay(
          value !== null && value !== undefined && value !== ''
            ? String(value)
            : ''
        )
      }
      return
    }

    const result = evalArithmetic(trimmed)
    if (result !== null) {
      setDisplay(String(result))
      onChange(result)
    } else {
      // Invalid expression — revert to last known good value
      setDisplay(
        value !== null && value !== undefined && value !== ''
          ? String(value)
          : ''
      )
    }
  }

  function handleChange(e) {
    setDisplay(e.target.value)
  }

  function handleBlur(e) {
    evaluate(display)
    onBlur?.(e)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      evaluate(display)
    } else if (e.key === 'Escape') {
      setDisplay(
        value !== null && value !== undefined && value !== ''
          ? String(value)
          : ''
      )
    }
  }

  // Subtle visual affordance while the user is typing an expression.
  const isExpression = looksLikeExpression(display)
  const expressionClass = isExpression
    ? ' ring-1 ring-teal-500/50 border-teal-600/60'
    : ''

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className}${expressionClass}`}
      placeholder={placeholder ?? '0'}
      disabled={disabled}
    />
  )
}
